import { gql } from '@apollo/client';
import config from '@config';
import client from '@graphql/apollo-client';
import { TrackerProduct } from '@xFrame4/common/Tracker';
import xObject from '@xFrame4/common/xObject';

/**
 * Klaviyo Helper
 * https://developers.klaviyo.com/en/docs/javascript_api
 */
export class KlaviyoHelper extends xObject
{
    /** The singleton instance. */
    protected static _instance?: KlaviyoHelper;

    //#region Singleton
    protected constructor()
    {
        super();

        this.init();
    }

    static get instance(): KlaviyoHelper
    {
        if (this._instance === undefined) this._instance = this.createInstance();

        return this._instance;
    }

    protected static createInstance(): KlaviyoHelper
    {
        return new KlaviyoHelper();
    }
    //#endregion

    /**
     * Load the Klaviyo script.
     */
    protected init(): Promise<void>
    {
        return new Promise(resolve =>
        {
            console.log('Loading Klaviyo script...');

            const script = document.createElement('script');
            script.src = 'https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=' + config.klaviyoPublicApiKey;
            script.onload = () =>
            {
                // wait for the klaviyo object to be available
                this.waitForKlaviyo().then(() => {
                    console.log('Klaviyo script loaded.');
                    this.fire('loaded');
                    resolve();
                });
            };
            document.body.appendChild(script);
        });
    }

    /**
     * Wait for the klaviyo object to be available.
     */
    async waitForKlaviyo(): Promise<boolean>
    {
        return new Promise<boolean>((resolve) => {
            const interval = setInterval(() => {
                console.log('Waiting for Klaviyo object...');
                if (typeof (window as any).klaviyo !== 'undefined')
                {
                    console.log('Klaviyo object is available.');
                    clearInterval(interval);
                    resolve(true);
                }
            }, 200);
        });
    }

    /**
     * Identiy a user in Klaviyo.
     * https://developers.klaviyo.com/en/docs/javascript_api#api-basics
     */
    identify(klaviyoUser: KlaviyoUser): Promise<void>
    {
        return new Promise(resolve =>
        {
            console.log('Identifying user in Klaviyo...', klaviyoUser);

            klaviyo.identify({
                $email: klaviyoUser.email,
                $external_id: klaviyoUser.externalId,
                $phone_number: klaviyoUser.phoneNumber,
                $first_name: klaviyoUser.firstName,
                $last_name: klaviyoUser.lastName,
            }, () =>
            {
                console.log('User identified in Klaviyo.');
                resolve();
            });
        });
    }

    /**
     * Track an event in Klaviyo.
     * https://developers.klaviyo.com/en/docs/javascript_api#track-events-and-actions
     * 
     * @param eventName The name of the event.
     * @param properties The properties of the event.
     * @param klaviyoUser The user to identify.
     */
    async trackEvent(eventName: string, properties: any = {}, klaviyoUser?: KlaviyoUser)
    {
        console.log('Tracking event in Klaviyo...', eventName, properties);

        if (klaviyoUser) await this.identify(klaviyoUser);

        console.log('Pushing event to Klaviyo...', eventName, properties);
        klaviyo.push(['track', eventName, properties]);
    }

    /**
     * Track a site searched in Klaviyo (Searched Site).
     */
    async trackSearchedSite(klavioSearchAction: KlaviyoSearchAction, klaviyoUser?: KlaviyoUser)
    {
        let properties: any = {
            SearchTerm: klavioSearchAction.searchTerm,
            'SearchTerm (autocorrected)': klavioSearchAction.searchTermCorrected,
            ReturnedResults: klavioSearchAction.returnedResults,
        };

        await this.trackEvent('Searched Site', properties, klaviyoUser);
    }

    /**
     * Track a viewed category in Klaviyo (Viewed Category).
     */
    async trackViewedCategory(klaviyoProductCategory: KlaviyoProductCategory, klaviyoUser?: KlaviyoUser)
    {
        let properties: any = {
            CategoryID: klaviyoProductCategory.id,
            CategoryName: klaviyoProductCategory.name,
            ImageURL: klaviyoProductCategory.imageUrl,
            URL: klaviyoProductCategory.url,
        };

        await this.trackEvent('Viewed Category', properties, klaviyoUser);
    }

    /**
     * Track a viewed product in Klaviyo (Viewed Product).
     * https://developers.klaviyo.com/en/docs/guide_to_integrating_a_platform_without_a_pre_built_klaviyo_integration#viewed-product-tracking-snippet
     * 
     * @param product Represents the product being viewed.
     * @param klaviyoUser The user to identify.
     */
    async trackViewedProduct(product: KlaviyoProduct, klaviyoUser?: KlaviyoUser)
    {
        let properties: any = {
            ProductID: product.productID,
            ProductName: product.productName,
            SKU: product.SKU,
            Categories: product.categories,
            URL: product.url,
            ImageURL: product.imageUrl,
            Brand: product.brand,
            Price: product.price,
        };
        
        await this.trackEvent('Viewed Product', properties, klaviyoUser);
    }

    /**
     * Track added to cart event in Klaviyo (Added to Cart).
     * https://developers.klaviyo.com/en/docs/guide_to_integrating_a_platform_without_a_pre_built_klaviyo_integration#added-to-cart-tracking-snippet
     * 
     * @param order Represents the existing cart data.
     * @param product Represents the product being added to the cart.
     * @param klaviyoUser The user to identify.
     */
    async trackAddedToCart(order: KlaviyoOrder, product: KlaviyoProduct, klaviyoUser?: KlaviyoUser)
    {
        let properties: any = {
            AddedItemProductName: product.productName,
            AddedItemProductID: product.productID,
            AddedItemSKU: product.SKU,
            AddedItemCategories: product.categories,
            AddedItemURL: product.url,
            AddedItemBrand: product.brand,
            AddedItemPrice: product.price,
            AddedItemQuantity: product.quantity,
            CheckoutURL: config.appUrl + 'cos-de-cumparaturi',
            Items: order.products?.map(product => ({
                ProductID: product.productID,
                ProductName: product.productName,
                SKU: product.SKU,
                Quantity: product.quantity,
                ItemPrice: product.price,
                RowTotal: (product.price !== undefined && product.quantity !== undefined) ? product.price * product.quantity : undefined,
                ProductURL: product.url,
                ImageURL: product.imageUrl,
                ProductCategories: product.categories,
            }))
        }

        await this.trackEvent('Added to Cart', properties, klaviyoUser);
    }

    /**
     * Track started checkout event in Klaviyo (Started Checkout).
     * https://developers.klaviyo.com/en/docs/guide_to_integrating_a_platform_without_a_pre_built_klaviyo_integration#started-checkout
     * 
     * @param order Represents the existing cart data.
     * @param eventId The event ID.
     * @param klaviyoUser The user to identify.
     */
    async trackStartedCheckout(order: KlaviyoOrder, eventId?: string, klaviyoUser?: KlaviyoUser)
    {
        let properties: any = {
            event_id: eventId,
            $value: order.value,
            CheckoutURL: config.appUrl + 'detalii-facturare-livrare',
            ItemNames: order.itemNames,
            Categories: order.categories,
            Items: order.products?.map(product => ({
                ProductID: product.productID,
                ProductName: product.productName,
                SKU: product.SKU,
                Quantity: product.quantity,
                ItemPrice: product.price,
                RowTotal: (product.price !== undefined && product.quantity !== undefined) ? product.price * product.quantity : undefined,
                ProductURL: product.url,
                ImageURL: product.imageUrl,
                ProductCategories: product.categories,
            }))
        };

        this.trackEvent('Started Checkout', properties, klaviyoUser);
    }

    /**
     * Create a Klaviyo profile with the GraphQL API.
     */
    async createProfile(klaviyoUser: KlaviyoUser)
    {
        let query = `
            mutation CreateKlaviyoProfile($input: KlaviyoUserInput!) {
                createKlaviyoProfile(input: $input) {
                    success
                    id
                }
            }
        `;

        let result = await client.mutate({
            mutation: gql(query),
            variables: {
                input: klaviyoUser
            },
        });

        if (result.data.createKlaviyoProfile.success)
        {
            return result.data.createKlaviyoProfile.id;
        }
        else
        {
            return null;
        }
    }

    /**
     * Subscribe a Klaviyo profile to a list with the GraphQL API.
     * 
     * @param listName The name of the list.
     * @param listId The ID of the list.
     * @param email The email of the user.
     * @param phoneNumber The phone number of the user.
     */
    async subscribeKlaviyoProfileToList(listName?: string, listId?: string, email?: string, phoneNumber?: string)
    {
        let query = `
            mutation SubscribeKlaviyoProfileToList($listName: String, $listId: String, $email: String, $phoneNumber: String) {
                subscribeKlaviyoProfileToList(listName: $listName, listId: $listId, email: $email, phoneNumber: $phoneNumber)
            }
        `;

        let result = await client.mutate({
            mutation: gql(query),
            variables: {
                listName: listName,
                listId: listId,
                email: email,
                phoneNumber: phoneNumber,
            },
        });

        return result.data.subscribeKlaviyoProfileToList as boolean;
    }

    /**
     * Create a Klaviyo product from a Tracker product.
     */
    static klaviyoProductFromTrackerProduct(trackerProduct: TrackerProduct)
    {
        return {
            productName: trackerProduct.name,
            productID: trackerProduct.id,
            SKU: trackerProduct.id,
            categories: [trackerProduct.category],
            brand: trackerProduct.brand,
            price: trackerProduct.price,
            quantity: trackerProduct.quantity,
            url: trackerProduct.url,
            imageUrl: trackerProduct.imageUrl,
        } as KlaviyoProduct;
    }
}

export type KlaviyoUser = {
    id?: string
    email?: string;
    externalId?: number;
    phoneNumber?: string;
    firstName?: string;
    lastName?: string;
    location?: KlaviyoLocation;
    properties?: any;
}

export type KlaviyoLocation = {
    address?: string;
    city?: string;
    region?: string;
    country?: string;
    zipCode?: string;
}

export type KlaviyoSearchAction = {
    searchTerm?: string;
    searchTermCorrected?: string;
    returnedResults?: number;
}

export type KlaviyoProductCategory = {
    id?: number;
    name?: string;
    imageUrl?: string;
    url?: string;
}

export type KlaviyoProduct = {
    productName?: string;
    productID?: string;
    SKU?: string;
    categories?: string[];
    imageUrl?: string;
    url?: string;
    brand?: string;
    price?: number;
    quantity?: number;
}

export type KlaviyoOrder = {
    itemNames?: string[];
    categories?: string[];
    checkoutUrl?: string;
    value?: number;
    products?: KlaviyoProduct[];
}

export default KlaviyoHelper;